/**
 * bookingLabel.ts
 * Shared label logic for a machine-schedule booking bar + its detail drawer, so the
 * standalone Machine Schedule, the scenario Phase-1 machine grid, and the sidebar all
 * label a booking the SAME way (2026-06-23).
 *
 * Decision (Gigi): the BAR shows the WORK ORDER's descriptive name with the WO number
 * in brackets — "Kemano T2 Tunnel Rock Testing (WO-2026-089)" — and the TEST name moves
 * to the sidebar. WorkOrderName is often blank, so we fall back to the Project name (the
 * meaningful descriptive name) and finally the test name. Quote-sourced bookings show the
 * quote title + number, else just the number. Maintenance/Calibration events show the
 * event type.
 */

import type { MachineBookingCalendar } from '../types/schedule';
import type { TaskWithBookings } from '../services/scheduleTasks';

/** The booking's reference number — WO id for WO bookings, quote number for quotes. */
export function bookingRefNumber(b: MachineBookingCalendar): string | null {
  return b.WorkOrderNo ?? b.QuoteNumber ?? null;
}

/** The test name on the booking's linked ScheduleTask (now shown in the sidebar). */
export function bookingTestName(b: MachineBookingCalendar): string | null {
  return b.TaskName ?? null;
}

/**
 * The PRIMARY bar / drawer-heading label.
 *  - Event (Maintenance/Calibration/OutOfService) → the event type.
 *  - Work-order booking → WorkOrderName ?? ProjectName ?? TestName, with "(WO-id)" appended.
 *  - Quote booking → QuoteTitle with "(Q-number)" appended, else just the quote number.
 *  - Otherwise → the test name, the source, or a generic fallback.
 */
export function bookingPrimaryLabel(b: MachineBookingCalendar): string {
  if (b.EventType) return b.EventType;

  // Work-order booking (descriptive name in front, WO number in brackets).
  if (b.WorkOrderNo) {
    const desc = b.WorkOrderName ?? b.ProjectName ?? b.TaskName ?? null;
    return desc ? `${desc} (${b.WorkOrderNo})` : b.WorkOrderNo;
  }

  // Quote booking (quote title in front when present, else just the number).
  if (b.QuoteNumber) {
    return b.QuoteTitle ? `${b.QuoteTitle} (${b.QuoteNumber})` : b.QuoteNumber;
  }

  return b.TaskName ?? b.Source ?? 'booking';
}

/**
 * The WORK label for a Phase-2 scenario task's booking bars — the work-order/project
 * (or quote) the test belongs to, leading with the descriptive name and the reference
 * number in brackets, mirroring the Machine Schedule bar (`bookingPrimaryLabel`):
 *   - WorkOrder task → "WorkOrderName ?? ProjectName (WO-id)", else just the WO id.
 *   - Quote task     → the quote number.
 *   - Manual / no work info → null (the bar falls back to the machine name).
 * The TEST name stays as the row label (the Phase-2 analogue of "test moves to the
 * sidebar"). Returns null so callers can fall back cleanly.
 */
export function taskWorkLabel(task: TaskWithBookings): string | null {
  if (task.SourceType === 'WorkOrder') {
    const desc = task.WorkOrderName ?? task.ProjectName ?? null;
    if (task.WorkOrderNo) return desc ? `${desc} (${task.WorkOrderNo})` : task.WorkOrderNo;
    return desc; // no WO id yet — show whatever descriptive name we have
  }
  if (task.SourceType === 'Quote' && task.QuoteNumber) {
    return task.QuoteNumber;
  }
  return null;
}
